import { useEffect } from "react";
import { useClerk, useOrganization, useOrganizationList, useUser } from "@clerk/react";
import { useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";

import { useI18n } from "~/i18n";
import { resolveOrganizationLogoUrl } from "../onboarding/MorrowOnboarding.logic";
import {
  OrganizationAvatar,
  PersonalAccountAvatar,
  SidebarWorkspaceAvatar,
  useCompanyLogoFallback,
} from "../org/organizationShared";
import { RenderErrorBoundary } from "../RenderErrorBoundary";
import { useUserProfile, ensureUserAvatarSeed } from "../workspace/morrowUserProfileStore";
import { SidebarMenuButton, useSidebar } from "../ui/sidebar";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "../ui/menu";

/** Personal-account sentinel for org switching (Clerk uses `null`). */
const PERSONAL_VALUE = "__personal__";

/**
 * Sidebar account control: user avatar opens a unified menu for personal and
 * organization accounts — switch workspace, create an org, manage the active
 * account, or open account settings. Replaces the separate org switcher row and
 * the old direct-to-settings avatar click.
 */
export function MorrowUserButton() {
  const { t } = useI18n();
  const { user } = useUser();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const clerk = useClerk();
  const { isMobile, setOpenMobile } = useSidebar();

  const displayName =
    user?.fullName?.trim() ||
    user?.primaryEmailAddress?.emailAddress?.trim() ||
    t("sidebar.account");

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  const openAccountSettings = () => {
    closeMobileSidebar();
    void navigate({ to: "/settings/general", hash: "account", hashScrollIntoView: false });
  };

  const openUserProfile = () => {
    closeMobileSidebar();
    void clerk.openUserProfile();
  };

  const openOrganizationSettings = () => {
    closeMobileSidebar();
    void navigate({ to: "/settings/organization" });
  };

  const openCreateOrganization = () => {
    closeMobileSidebar();
    void clerk.openCreateOrganization({
      afterCreateOrganizationUrl: "/settings/organization",
    });
  };

  const userProfile = useUserProfile();

  useEffect(() => {
    ensureUserAvatarSeed();
  }, []);

  return (
    <Menu>
      <MenuTrigger
        render={
          <SidebarMenuButton aria-label={t("sidebar.account")} size="icon">
            <SidebarWorkspaceAvatar
              userName={displayName}
              {...(userProfile.avatarSeed ? { avatarSeed: userProfile.avatarSeed } : {})}
              size="xs"
            />
          </SidebarMenuButton>
        }
      />
      <MenuPopup align="start" className="min-w-52" side="top">
        <RenderErrorBoundary fallback={<OrganizationsDisabledAccountMenu />}>
          <AccountMenuOrganizationBody
            onCreateOrganization={openCreateOrganization}
            onManageOrganization={openOrganizationSettings}
            onManagePersonal={openUserProfile}
          />
        </RenderErrorBoundary>
        <MenuSeparator />
        <MenuItem onClick={openAccountSettings}>{t("sidebar.account")}</MenuItem>
      </MenuPopup>
    </Menu>
  );
}

function AccountMenuOrganizationBody({
  onCreateOrganization,
  onManageOrganization,
  onManagePersonal,
}: {
  readonly onCreateOrganization: () => void;
  readonly onManageOrganization: () => void;
  readonly onManagePersonal: () => void;
}) {
  const { t } = useI18n();
  const { organization } = useOrganization();

  return (
    <>
      <AccountMenuOrganizationSection />
      <MenuSeparator />
      <MenuItem onClick={onCreateOrganization}>
        <PlusIcon />
        {t("settings.organization.createAction")}
      </MenuItem>
      <MenuItem onClick={organization ? onManageOrganization : onManagePersonal}>
        {organization
          ? t("settings.organization.manageAction")
          : t("settings.account.manageAction")}
      </MenuItem>
    </>
  );
}

function AccountMenuOrganizationSection() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const companyLogoFallback = useCompanyLogoFallback();
  const {
    isLoaded: listLoaded,
    setActive,
    userMemberships,
  } = useOrganizationList({
    userMemberships: true,
  });
  const { isMobile, setOpenMobile } = useSidebar();

  if (!listLoaded) return null;

  const memberships = userMemberships.data ?? [];
  const orgs = memberships.map((entry) => entry.organization);
  const activeValue = organization?.id ?? PERSONAL_VALUE;

  const handleSwitch = (value: string) => {
    if (value === activeValue || !setActive) return;
    if (isMobile) setOpenMobile(false);
    void setActive({ organization: value === PERSONAL_VALUE ? null : value }).then(() => {
      void navigate({ to: "/" });
    });
  };

  return (
    <MenuGroup>
      <MenuGroupLabel>{t("settings.organization.switchTitle")}</MenuGroupLabel>
      <MenuRadioGroup value={activeValue} onValueChange={handleSwitch}>
        <MenuRadioItem closeOnClick value={PERSONAL_VALUE}>
          <span className="flex min-w-0 items-center gap-2">
            <PersonalAccountAvatar className="size-5" />
            <span className="truncate">{t("settings.organization.personal")}</span>
          </span>
        </MenuRadioItem>
        {orgs.map((org) => (
          <MenuRadioItem closeOnClick key={org.id} value={org.id}>
            <span className="flex min-w-0 items-center gap-2">
              <OrganizationAvatar
                className="size-5"
                logoUrl={resolveOrganizationLogoUrl(org, companyLogoFallback)}
                name={org.name}
              />
              <span className="truncate">{org.name}</span>
            </span>
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>
    </MenuGroup>
  );
}

function OrganizationsDisabledAccountMenu() {
  const { t } = useI18n();
  return (
    <MenuGroup>
      <MenuGroupLabel>{t("settings.organization.personal")}</MenuGroupLabel>
      <MenuItem disabled>{t("settings.organization.disabled")}</MenuItem>
    </MenuGroup>
  );
}
